import { Fade, Tab, Tabs, ThemeProvider } from '@mui/material';
import type { Currency, FixedProductInput } from '@weft/types';
import { DEFAULT_CURRENCY } from '@weft/types';
import { validateEnum, withDefault } from '@weft/utils';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { muiDefaultTheme } from '../../../../../assets/themes/default-theme';
import type { TabRecord } from '../../../../../utils/types/tab-record';
import { TabPanel } from '../../../../plans/CreatePlanPage/create-plan-page-components/CreatePlanPageTabs/create-plan-page-tabs-components/TabPanel';
import { useCreateInvoiceContext } from '../../create-invoice-page-contexts/create-invoice-context/create-invoice-context';
import type { CreateInvoiceTab } from '../../create-invoice-page-contexts/create-invoice-context/create-invoice-context-types/create-invoice-tab';
import { CREATE_INVOICE_TABS } from '../../create-invoice-page-contexts/create-invoice-context/create-invoice-context-types/create-invoice-tab';
import type { InvoiceFormInput } from '../CreateInvoiceForm/create-invoice-form-types/invoice-form-input';
import { CreateInvoicePageFixedChargesTabContent } from './create-invoice-page-tabs-components/CreateInvoicePageFixedChargesTabContent';

export const CreateInvoicePageTabs: React.FC = () => {
  const { watch } = useFormContext<InvoiceFormInput>();
  const { createInvoiceTab: currentTab, setCreateInvoiceTab: setCurrentTab } = useCreateInvoiceContext();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    validateEnum(newValue, CREATE_INVOICE_TABS);
    setCurrentTab(newValue);
  };

  const createInvoiceTabs = getCreateInvoiceTabs({
    fixedProducts: [...withDefault(watch('fixedProducts'), [])],
    currency: withDefault(watch('settings.currency'), DEFAULT_CURRENCY),
  });

  return (
    <>
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        textColor="primary"
        indicatorColor="primary"
      >
        {Object.entries(createInvoiceTabs).map(([createPlanTab, { id, label, selectorAttribute }]) => (
          <Tab
            key={id}
            label={label}
            value={createPlanTab}
            className={twMerge('normal-case group font-normal', createPlanTab === currentTab ? 'is-selected' : 'text-[#94A0A4]')}
            disabled={createPlanTab !== CREATE_INVOICE_TABS.serviceFees}
            {...selectorAttribute}
          />
        ))}
      </Tabs>

      {Object.entries(createInvoiceTabs).map(([createPlanTab, { id, render }]) => (
        <TabPanel
          key={id}
          hidden={currentTab !== createPlanTab}
          className="h-full"
        >
          <Fade in={currentTab === createPlanTab}>
            <div className="h-full w-full">
              {render}
            </div>
          </Fade>
        </TabPanel>
      ))}
    </>
  );
};

type GetCreateInvoiceTabsPayload = {
  fixedProducts: FixedProductInput[];
  currency: Currency;
};

export function getCreateInvoiceTabs({ fixedProducts, currency }: GetCreateInvoiceTabsPayload): TabRecord<CreateInvoiceTab> {
  return {
    [CREATE_INVOICE_TABS.serviceFees]: {
      id: 0,
      render: (
        <ThemeProvider theme={muiDefaultTheme}>
          <CreateInvoicePageFixedChargesTabContent
            fixedProducts={fixedProducts}
            currency={currency}
          />
        </ThemeProvider>
      ),
      label: 'Service fees',
    },
    [CREATE_INVOICE_TABS.tierAndVolumePricing]: {
      id: 1,
      render: <div>usages</div>,
      label: 'Usage-based fees',
    },
    [CREATE_INVOICE_TABS.promotions]: {
      id: 2,
      render: <div>promotions</div>,
      label: 'Credits & Discounts',
    },
  };
}